import { RecordingStatus } from '@heidi/schema/ml-scribe';

import { GET_SESSION_BY_ID, type QuerySessionResponse } from '../../contracts';
import { getSessionDataConfig } from '../config';
import { areAllTranscriptsEmpty } from './are-all-transcripts-empty';

/**
 * The single "is this session's transcript gone?" rule, shared by the React
 * hook (UI) and the non-hook cache predicate (belt). Mirrors
 * `useTranscriptViewModel`'s `deleted` branch: the marker only counts while idle
 * with no transcript content, since the backend doesn't clear
 * `transcript_deleted_at` on re-record.
 *
 * The hook and belt read different session cache keys (the hook falls back to
 * the team-member key for reviewers), so they can differ for that cohort —
 * masked today by `isReviewBlocked`.
 */
export const computeIsTranscriptDeleted = ({
  transcriptDeletedAt,
  recordingStatus,
  hasTranscriptContent,
}: {
  transcriptDeletedAt: string | null | undefined;
  recordingStatus: RecordingStatus | null | undefined;
  hasTranscriptContent: boolean;
}): boolean => {
  if (!transcriptDeletedAt) return false;
  if (
    recordingStatus === RecordingStatus.RECORDING ||
    recordingStatus === RecordingStatus.PAUSED
  ) {
    return false;
  }
  return !hasTranscriptContent;
};

/**
 * Non-hook, cache-only variant for the generation chokepoints, which run
 * outside React. Not flag-gated on purpose: the marker is only set by the
 * flag-gated deletion flow, and blocking a genuinely deleted transcript stays
 * correct even after the flag is turned off.
 */
export const isTranscriptDeletedInCache = (sessionId: string): boolean => {
  if (!sessionId) return false;
  const session = getSessionDataConfig()
    .getQueryClient()
    .getQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }]);
  return computeIsTranscriptDeleted({
    transcriptDeletedAt: session?.transcript_deleted_at,
    recordingStatus: session?.recording_status,
    hasTranscriptContent: !areAllTranscriptsEmpty(sessionId),
  });
};
